import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { keys } from './keys';
import { invalidate, type QueryOptions } from './query-client';
import { request } from './client';
import type {
  ListQuery,
  NewAccount,
  Paginated,
  Tenant,
  TenantCreated,
  TenantMember,
  TenantOverview,
  TenantRole,
  TenantStatus,
} from './types';

export const tenantsApi = {
  /** `slug` narrows to the one tenant with that URL handle (the staff tenant page). */
  list: (
    query: ListQuery & { status?: TenantStatus; accountManagerId?: string; slug?: string } = {},
  ) => request<Paginated<Tenant>>('/tenants', { query }),
  /** One tenant: staff any, a tenant account its own (anything else is a 404). */
  get: (tenantId: string) => request<Tenant>(`/tenants/${tenantId}`),
  /** The tenant page in one call: tenant, members, domains, plans, features, checklist, WhatsApp. */
  overview: (tenantId: string) => request<TenantOverview>(`/tenants/${tenantId}/overview`),
  /** Staff: rename, change status or the owner; admins also reassign (or clear) the account manager. */
  update: (
    tenantId: string,
    body: {
      name?: string;
      status?: TenantStatus;
      tenantOwnerId?: string;
      accountManagerId?: string | null;
      version?: number;
    },
  ) => request<Tenant>(`/tenants/${tenantId}`, { method: 'PATCH', body }),
  /** Staff: offboard the tenant (its domains, mailboxes, plans and campaigns go with it). */
  remove: (tenantId: string) => request<void>(`/tenants/${tenantId}`, { method: 'DELETE' }),
  /**
   * Onboard an organization (any staff role). `ownerEmail` is put on the sign-up list and the
   * account that signs up with it becomes the owner; with `domain`, the company's email domain
   * is onboarded for sign-up and assigned for email.
   */
  create: (body: { name: string; slug: string; ownerEmail?: string; domain?: string }) =>
    request<TenantCreated>('/tenants', { method: 'POST', body }),
  /** Accounts on the tenant, owners first. */
  members: (tenantId: string) => request<TenantMember[]>(`/tenants/${tenantId}/members`),
  /** Invite an account (a temporary password is emailed); 409 once the tenant has three. */
  invite: (tenantId: string, body: NewAccount & { role?: TenantRole }) =>
    request<TenantMember>(`/tenants/${tenantId}/members`, { method: 'POST', body }),
  updateMember: (tenantId: string, userId: string, role: TenantRole) =>
    request<TenantMember>(`/tenants/${tenantId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    }),
  removeMember: (tenantId: string, userId: string) =>
    request<void>(`/tenants/${tenantId}/members/${userId}`, { method: 'DELETE' }),
};

type TenantsQuery = NonNullable<Parameters<typeof tenantsApi.list>[0]>;
type CreateTenantBody = Parameters<typeof tenantsApi.create>[0];
type UpdateTenantBody = Parameters<typeof tenantsApi.update>[1];
type InviteMemberBody = Parameters<typeof tenantsApi.invite>[1];

export function useTenantList(query: TenantsQuery = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.list(query),
    queryFn: () => tenantsApi.list(query),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** One tenant by id. Idle until the id is known. */
export function useTenant(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.detail(tenantId),
    queryFn: tenantId ? () => tenantsApi.get(tenantId) : skipToken,
    ...options,
  });
}

/** Everything the tenant page shows, in one request. Idle until the id is known. */
export function useTenantOverview(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.overview(tenantId),
    queryFn: tenantId ? () => tenantsApi.overview(tenantId) : skipToken,
    ...options,
  });
}

/** Accounts on a tenant, owners first. Idle until the tenant id is known. */
export function useTenantMembers(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.members(tenantId),
    queryFn: tenantId ? () => tenantsApi.members(tenantId) : skipToken,
    ...options,
  });
}

/**
 * Onboard an organization. The tenant list changes; with `ownerEmail` or
 * `domain`, so do the sign-up list and the email domains.
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantBody) => tenantsApi.create(body),
    onSuccess: () =>
      invalidate(queryClient, keys.tenants.all, keys.users.all, keys.email.all, keys.whitelist.all),
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, body }: { tenantId: string; body: UpdateTenantBody }) =>
      tenantsApi.update(tenantId, body),
    onSuccess: () => invalidate(queryClient, keys.tenants.all),
  });
}

/** Offboarding touches the tenant's accounts, domains and plans too. */
export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => tenantsApi.remove(tenantId),
    onSuccess: () =>
      invalidate(
        queryClient,
        keys.tenants.all,
        keys.users.all,
        keys.email.all,
        keys.plans.all,
        keys.whitelist.all,
      ),
  });
}

/** Membership writes refresh the tenant's members and the directory (the account's role changed). */
function useMemberMutation<TVars, TData>(
  tenantId: string,
  mutationFn: (vars: TVars) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidate(queryClient, keys.tenants.members(tenantId), keys.users.all),
  });
}

export function useInviteMember(tenantId: string) {
  return useMemberMutation(tenantId, (body: InviteMemberBody) => tenantsApi.invite(tenantId, body));
}

export function useUpdateMember(tenantId: string) {
  return useMemberMutation(tenantId, ({ userId, role }: { userId: string; role: TenantRole }) =>
    tenantsApi.updateMember(tenantId, userId, role),
  );
}

export function useRemoveMember(tenantId: string) {
  return useMemberMutation(tenantId, (userId: string) => tenantsApi.removeMember(tenantId, userId));
}
